export const PROJECT_DEPLOYMENT_STATUS = {
  not_requested: {
    label: "Pendiente",
    severity: "default",
    description: "La publicación automática todavía no fue solicitada.",
  },
  not_configured: {
    label: "Render sin configurar",
    severity: "warning",
    description: "Faltan credenciales o variables de Render en el backend.",
  },
  provisioning: {
    label: "Publicando",
    severity: "info",
    description: "Render está construyendo y preparando la página pública.",
  },
  live: {
    label: "Publicada",
    severity: "success",
    description: "La página ya está disponible públicamente.",
  },
  failed: {
    label: "Falló la publicación",
    severity: "error",
    description: "Render no pudo completar el último despliegue.",
  },
};

export function normalizeProjectPublicUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const parsed = new URL(raw.includes("://") ? raw : `https://${raw}`);
    return parsed.toString().replace(/\/$/, "");
  } catch {
    return "";
  }
}

export function getProjectRenderUrl(project) {
  return normalizeProjectPublicUrl(project?.deployment?.url);
}

export function getProjectPublicUrl(project) {
  const renderUrl = getProjectRenderUrl(project);
  if (renderUrl) return renderUrl;

  const domain = Array.isArray(project?.domains)
    ? project.domains.find(Boolean)
    : "";

  return normalizeProjectPublicUrl(domain);
}

export function getProjectPublicDomain(project) {
  const url = getProjectPublicUrl(project);
  if (!url) return "";

  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

export function getProjectDeploymentStatus(project) {
  const status = String(project?.deployment?.status || "not_requested");
  return PROJECT_DEPLOYMENT_STATUS[status]
    ? status
    : "not_requested";
}

export function getProjectDeploymentMeta(project) {
  const status = getProjectDeploymentStatus(project);
  return {
    status,
    ...PROJECT_DEPLOYMENT_STATUS[status],
  };
}

export function isProjectDeploymentPending(project) {
  return ["not_requested", "provisioning"].includes(
    getProjectDeploymentStatus(project)
  );
}

export function canRetryProjectDeployment(project) {
  return ["not_requested", "not_configured", "failed"].includes(
    getProjectDeploymentStatus(project)
  );
}

function absolutePreviewEndpoint(endpoint, apiBaseUrl) {
  const candidate = String(endpoint || "").trim();
  if (!candidate || typeof window === "undefined") return "";

  try {
    if (/^https?:\/\//i.test(candidate)) {
      return new URL(candidate).toString();
    }

    const origin = window.location.origin;
    const resolvedApiBase = new URL(String(apiBaseUrl || "/api"), origin);
    if (candidate.startsWith("/")) {
      return new URL(candidate, resolvedApiBase.origin).toString();
    }

    const baseWithSlash = `${resolvedApiBase.toString().replace(/\/+$/, "")}/`;
    return new URL(candidate, baseWithSlash).toString();
  } catch {
    return "";
  }
}

/**
 * Construye la dirección pública de una vista previa sin enviar el token al
 * servidor que sirve el HTML. El storefront recibe los datos en el fragmento
 * y luego consume el endpoint público mediante POST.
 */
export function buildProjectSecurePreviewUrl(project, preview, apiBaseUrl) {
  const publicUrl = getProjectPublicUrl(project);
  const token = String(preview?.token || "").trim();
  const projectKey = String(
    preview?.projectSlug || project?.slug || preview?.projectId || project?.id || project?._id || ""
  ).trim();
  const endpoint = absolutePreviewEndpoint(preview?.endpoint, apiBaseUrl);
  const draftRevision = Number(preview?.draftRevision || 0);

  if (!publicUrl || !token || !endpoint || !projectKey) return "";

  try {
    const target = new URL(publicUrl);
    const params = new URLSearchParams();
    params.set("wpPreview", "1");
    params.set("project", projectKey);
    params.set("previewToken", token);
    params.set("previewEndpoint", endpoint);
    params.set("previewMethod", String(preview?.method || "POST").toUpperCase());
    if (Number.isSafeInteger(draftRevision) && draftRevision > 0) {
      params.set("previewRevision", String(draftRevision));
      // La revisión también viaja como query no sensible para forzar una
      // navegación real cuando una preview ya abierta se sincroniza con otro
      // borrador. El token continúa únicamente en el hash y nunca llega al
      // servidor que entrega el HTML público.
      target.searchParams.set("wpPreviewRevision", String(draftRevision));
    }
    if (preview?.expiresAt) {
      params.set("previewExpiresAt", String(preview.expiresAt));
    }

    target.hash = `/?${params.toString()}`;
    return target.toString();
  } catch {
    return "";
  }
}

